import { CapacitorWifiConnect, ConnectState } from '@falconeta/capacitor-wifi-connect';

import { timeout } from '../../../utils/common/common-utils';
import {
  EVSE_AP_INITIAL_CONNECT_DELAY_MS,
  EVSE_AP_READY_MAX_WAIT_MS,
  EVSE_AP_RECONNECT_INTERVAL_MS,
} from '../constants';

type ConnectFn = (ssid: string, password: string) => Promise<ConnectState>;
type GetSsidFn = () => Promise<string | undefined>;
type OnConnectFailedFn = () => Promise<boolean>;
type IsApReachableFn = () => Promise<boolean>;

export type EvseApReadyResult =
  | { ok: true; ssidUnverified?: boolean }
  | {
      ok: false;
      reason: 'timeout' | 'cancelled';
    };

type WaitForEvseApOptions = {
  ssid: string;
  password: string;
  maxWaitMs?: number;
  initialConnectDelayMs?: number;
  reconnectIntervalMs?: number;
  connectFn?: ConnectFn;
  getSsidFn?: GetSsidFn;
  onConnectFailed?: OnConnectFailedFn;
  // iOS hides the SSID when Precise Location is off; a reachable EVSE API proves the join instead.
  isApReachableFn?: IsApReachableFn;
};

// Android's ConnectivityManager.requestNetwork(request, callback, handler) overload has no
// timeout and can leave the plugin's promise pending forever if the AP never becomes
// reachable, which would otherwise block this function from ever re-checking `deadline`.
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('EVSE AP connect attempt timed out')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

async function connect(ssid: string, password: string) {
  const result = await CapacitorWifiConnect.secureConnect({ ssid, password, saveNetwork: false });
  return result.value;
}

async function getSsid() {
  const currentNetwork = await CapacitorWifiConnect.getDeviceSSID();
  return currentNetwork?.value;
}

export async function waitForEvseApReady({
  ssid,
  password,
  maxWaitMs = EVSE_AP_READY_MAX_WAIT_MS,
  initialConnectDelayMs = EVSE_AP_INITIAL_CONNECT_DELAY_MS,
  reconnectIntervalMs = EVSE_AP_RECONNECT_INTERVAL_MS,
  connectFn = (s, p) => connect(s, p),
  getSsidFn = () => getSsid(),
  onConnectFailed,
  isApReachableFn,
}: WaitForEvseApOptions): Promise<EvseApReadyResult> {
  const reconnectDelayMs = Math.max(1, reconnectIntervalMs);
  const deadline = Date.now() + maxWaitMs;
  let lastConnectAt = 0;
  let retryApproved = false;
  let isApReachable = false;

  async function tryConnect(tolerateWifiDisabled: boolean) {
    lastConnectAt = Date.now();
    try {
      const connectState = await connectFn(ssid, password);
      if (connectState === ConnectState.WifiDisabled && isApReachableFn) {
        await timeout(3000);
        isApReachable = await isApReachableFn().catch(() => false);
        if (isApReachable) {
          return true;
        }
      }
      // NEHotspotConfiguration.apply() can resolve before iOS updates the SSID it
      // reports, which this plugin surfaces as WifiDisabled. This is only a
      // transient state for the first connection attempt.
      if (connectState === ConnectState.WifiDisabled && tolerateWifiDisabled) {
        return true;
      }
      if (connectState !== ConnectState.Ok) {
        throw new Error(`EVSE AP connection returned ${connectState}`);
      }
    } catch (error) {
      // Connect can fail while the EVSE AP is still starting.
      return false;
    }

    return true;
  }

  async function approveRetry() {
    retryApproved = !onConnectFailed || (await onConnectFailed());
    return retryApproved;
  }

  if (initialConnectDelayMs > 0) {
    await timeout(initialConnectDelayMs);
  }

  try {
    const currentSsid = await getSsidFn();
    if (currentSsid === ssid) {
      return { ok: true };
    }
  } catch {
    // SSID lookup can fail transiently before the first connect attempt.
  }

  try {
    const initialConnectSucceeded = await withTimeout(tryConnect(true), EVSE_AP_READY_MAX_WAIT_MS);
    if (!initialConnectSucceeded && !(await approveRetry())) {
      return { ok: false, reason: 'cancelled' };
    }
  } catch (error) {
    return { ok: false, reason: 'timeout' };
  }

  if (isApReachable) {
    return { ok: true, ssidUnverified: true };
  }

  try {
    const currentSsid = await getSsidFn();
    if (currentSsid === ssid) {
      return { ok: true };
    }
  } catch {
    // SSID lookup can fail transiently after connect.
  }

  while (Date.now() <= deadline) {
    try {
      const currentSsid = await getSsidFn();
      if (currentSsid === ssid) {
        return { ok: true };
      }
    } catch {
      // SSID lookup can transiently fail while Wi-Fi services settle.
    }

    if (!retryApproved && !(await approveRetry())) {
      return { ok: false, reason: 'cancelled' };
    }

    if (retryApproved && Date.now() - lastConnectAt >= reconnectDelayMs) {
      retryApproved = false;
      if (!(await tryConnect(false)) && !(await approveRetry())) {
        return { ok: false, reason: 'cancelled' };
      }
      if (isApReachable) {
        return { ok: true, ssidUnverified: true };
      }
    }

    if (Date.now() < deadline) {
      await timeout(reconnectDelayMs);
    }
  }

  try {
    const currentSsid = await getSsidFn();
    if (currentSsid === ssid) {
      return { ok: true };
    }
  } catch {
    // SSID lookup can fail transiently after the last connect attempt.
  }

  return { ok: false, reason: 'timeout' };
}
